/**
 * Utility functions for hCaptcha error handling
 *
 * Provides type-safe error extraction and manipulation utilities
 * for working with unknown error types from the hCaptcha SDK.
 *
 * @module src/captcha/hcaptcha/utils
 */

/**
 * Safely extract error message from unknown error types
 *
 * Handles various error formats and safely extracts the error message
 * without throwing exceptions. Useful for processing errors from
 * external libraries that may not throw standard Error objects.
 *
 * @param error - Unknown error object from any source
 * @returns The extracted error message, or a fallback string
 *
 * @example
 * ```typescript
 * try {
 *   await someAsyncOperation();
 * } catch (error) {
 *   const message = extractErrorMessage(error);
 *   console.log(`Error: ${message}`);
 * }
 * ```
 */
export function extractErrorMessage(error: unknown): string {
  // Handle string errors
  if (typeof error === 'string') {
    return error;
  }

  // Handle Error instances
  if (error instanceof Error) {
    return error.message;
  }

  // Handle objects with message property
  if (error && typeof error === 'object' && 'message' in error) {
    return String((error as any).message);
  }

  // Fallback: convert to string
  return String(error) || 'Unknown error';
}

/**
 * Safely extract error name from unknown error types
 *
 * Handles various error formats and safely extracts the error name
 * without throwing exceptions. Useful for error classification and logging.
 *
 * @param error - Unknown error object from any source
 * @returns The extracted error name, or a fallback string
 *
 * @example
 * ```typescript
 * try {
 *   await someAsyncOperation();
 * } catch (error) {
 *   const name = extractErrorName(error);
 *   console.log(`Error type: ${name}`);
 * }
 * ```
 */
export function extractErrorName(error: unknown): string {
  // Handle Error instances
  if (error instanceof Error) {
    return error.name;
  }

  // Handle objects with name property
  if (error && typeof error === 'object' && 'name' in error) {
    return String((error as any).name);
  }

  // Fallback: return unknown
  return 'unknown_error';
}

/**
 * Extract both message and name from an unknown error type
 *
 * Convenience function that extracts both message and name in one call.
 * More efficient than calling both functions separately.
 *
 * @param error - Unknown error object from any source
 * @returns Object containing extracted message and name
 *
 * @example
 * ```typescript
 * const { message, name } = extractErrorDetails(error);
 * reportError({ errorMessage: message, errorName: name });
 * ```
 */
export function extractErrorDetails(error: unknown): {
  message: string;
  name: string;
} {
  return {
    message: extractErrorMessage(error),
    name: extractErrorName(error),
  };
}

/**
 * Check if error is an Error instance
 *
 * Type guard function for narrowing error types in catch blocks.
 *
 * @param error - Unknown value to check
 * @returns True if error is an Error instance
 *
 * @example
 * ```typescript
 * catch (error) {
 *   if (isError(error)) {
 *     console.log(error.message); // Safe to access message
 *   }
 * }
 * ```
 */
export function isError(error: unknown): error is Error {
  return error instanceof Error;
}

/**
 * Check if error is a custom error-like object
 *
 * Type guard for objects that have error properties but aren't Error instances.
 * Common with third-party APIs that use custom error objects.
 *
 * @param error - Unknown value to check
 * @returns True if error has typical error properties
 *
 * @example
 * ```typescript
 * catch (error) {
 *   if (isErrorLike(error)) {
 *     const { message, code } = error as any;
 *   }
 * }
 * ```
 */
export function isErrorLike(error: unknown): error is {message?: string; name?: string} {
  return error !== null && typeof error === 'object' && ('message' in error || 'name' in error);
}
